import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminLinkAction,
  AdminSupportingText,
  AdminAlertText,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  getQuoteRequestById,
  getQuoteRequestSourceDisplay,
} from '../../../data/quoteModerationScenarios';

const RECIPIENTS_GRID =
  'minmax(140px, 1.2fr) minmax(110px, 0.9fr) minmax(120px, 1fr) minmax(110px, 0.9fr)';

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayOrDash(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  return String(value);
}

function Field({ label, children }) {
  return (
    <div className="space-y-1 min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <AdminDetailFieldValue className="break-words">{children}</AdminDetailFieldValue>
    </div>
  );
}

function AppliedRuleSettingsBlock({ settings }) {
  if (!settings) {
    return <AdminDetailFieldValue>—</AdminDetailFieldValue>;
  }
  const conditions = Array.isArray(settings.conditions)
    ? settings.conditions.join('; ')
    : settings.conditions;
  const rows = [
    { label: 'Conditions', value: conditions },
    { label: 'Maximum Businesses', value: settings.maximumBusinesses },
    { label: 'Pro Businesses', value: settings.proBusinesses },
    { label: 'Basic Businesses', value: settings.basicBusinesses },
    { label: 'Best Match', value: settings.bestMatch },
    { label: 'Default Quote Radius', value: settings.defaultQuoteRadius },
    { label: 'Distribution Method', value: settings.distributionMethod },
  ].filter((row) => row.value !== null && row.value !== undefined && row.value !== '');

  if (!rows.length) return <AdminDetailFieldValue>—</AdminDetailFieldValue>;

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <Field key={row.label} label={row.label}>
          {displayOrDash(row.value)}
        </Field>
      ))}
    </div>
  );
}

function SnapshotFields({ snapshot }) {
  if (!snapshot) {
    return <AdminSupportingText className="text-mediumgray">No request snapshot retained.</AdminSupportingText>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Form Record">{displayOrDash(snapshot.formRecord)}</Field>
      <Field label="Form Version">{displayOrDash(snapshot.formVersion)}</Field>
      <Field label="Quote Title">{displayOrDash(snapshot.quoteTitle)}</Field>
      <Field label="Trade Category">{displayOrDash(snapshot.tradeCategory)}</Field>
      <Field label="Subcategory">{displayOrDash(snapshot.subcategory)}</Field>
      <Field label="Job Location">{displayOrDash(snapshot.jobLocation)}</Field>
      <Field label="Selected Quote Radius">{displayOrDash(snapshot.selectedQuoteRadius)}</Field>
      <Field label="Submitted On">{formatDateTime(snapshot.submittedOn)}</Field>
      <div className="md:col-span-2">
        <Field label="Job Description">{displayOrDash(snapshot.jobDescription)}</Field>
      </div>
      <div className="md:col-span-2">
        <Field label="Attachments">{displayOrDash(snapshot.attachments)}</Field>
      </div>
    </div>
  );
}

const QuoteRequestDetail = () => {
  const { requestId } = useParams();
  const location = useLocation();
  const [request, setRequest] = useState(location.state?.request || null);
  const [loading, setLoading] = useState(!location.state?.request);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (location.state?.request?.quoteRequestId) {
      setRequest(location.state.request);
      setLoading(false);
      return;
    }
    setLoading(true);
    const found = getQuoteRequestById(requestId);
    if (found) {
      setRequest(found);
      setError(null);
    } else {
      setRequest(null);
      setError('Quote Request not found.');
    }
    setLoading(false);
  }, [requestId, location.state]);

  const handleCopy = async (fieldKey, value) => {
    if (!value || value === '—') return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(fieldKey);
      window.setTimeout(() => setCopiedField(null), 1500);
    } catch {
      // ignore
    }
  };

  const renderCopyable = (fieldKey, value, label) => {
    const display = value || '—';
    const canCopy = Boolean(value && value !== '—');
    return (
      <AdminDetailFieldValue as="div" className="inline-flex items-center gap-1.5 min-w-0">
        <span className="truncate" title={display}>
          {display}
        </span>
        {canCopy ? (
          <button
            type="button"
            onClick={() => handleCopy(fieldKey, value)}
            className="inline-flex items-center justify-center text-mediumgray hover:text-primaryblue shrink-0"
            title={copiedField === fieldKey ? 'Copied' : `Copy ${label}`}
            aria-label={copiedField === fieldKey ? 'Copied' : `Copy ${label}`}
          >
            <i
              className={`fi ${
                copiedField === fieldKey ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
              } text-xs leading-none`}
              aria-hidden
            />
          </button>
        ) : null}
      </AdminDetailFieldValue>
    );
  };

  const customerId = request?.customer?.customerId || request?.customer?.id;
  const customerName = request?.customer?.name || '—';
  const customerUrl = customerId ? `/admin/users/customers/${customerId}` : null;
  const clarifications = request?.clarifications || [];
  const outcome = request?.outcome || null;
  const recipients = request?.recipients || [];
  const assessments = outcome?.assessments || [];

  return (
    <AdminLayout>
      <div className="min-w-0">
        <AdminPageTitle>Quote Request Detail</AdminPageTitle>

        {loading ? (
          <div className="space-y-4 mt-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-white animate-pulse rounded" />
            ))}
          </div>
        ) : error || !request ? (
          <div className="text-center py-10">
            <AdminAlertText className="text-alertred mb-4 block">
              {error || 'Quote Request not found.'}
            </AdminAlertText>
            <AdminLinkAction to="/admin/quotes/all">Back to Quotes</AdminLinkAction>
          </div>
        ) : (
          <div className="space-y-8 mt-6">
            {/* Header identity fields */}
            <DashboardMainContainer className="p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1 min-w-0">
                  <AdminDetailFieldLabel>Quote Request ID</AdminDetailFieldLabel>
                  {renderCopyable('quoteRequestId', request.quoteRequestId, 'Quote Request ID')}
                </div>
                <Field label="Customer">
                  {customerUrl ? (
                    <AdminLinkAction to={customerUrl}>{customerName}</AdminLinkAction>
                  ) : (
                    customerName
                  )}
                </Field>
                <div className="space-y-1 min-w-0">
                  <AdminDetailFieldLabel>Customer ID</AdminDetailFieldLabel>
                  {renderCopyable('customerId', customerId, 'Customer ID')}
                </div>
                <Field label="Request Source">
                  {getQuoteRequestSourceDisplay(request.requestSource)}
                </Field>
                <Field label="Quote Request Status">{displayOrDash(request.requestStatus)}</Field>
                <Field label="Submitted On">{formatDateTime(request.submittedOn)}</Field>
                {request.intendedBusiness ? (
                  <Field label="Intended Business">
                    {request.intendedBusiness.businessId ? (
                      <AdminLinkAction
                        to={`/admin/users/businesses/${request.intendedBusiness.businessId}`}
                      >
                        {request.intendedBusiness.businessName || request.intendedBusiness.businessId}
                      </AdminLinkAction>
                    ) : (
                      displayOrDash(request.intendedBusiness.businessName)
                    )}
                  </Field>
                ) : null}
              </div>
            </DashboardMainContainer>

            {/* Request snapshot */}
            <section className="space-y-3">
              <AdminPanelTitle className="!mb-0">Request</AdminPanelTitle>
              <DashboardMainContainer className="p-4 sm:p-6">
                <SnapshotFields snapshot={request.snapshot} />
              </DashboardMainContainer>
            </section>

            {/* Clarifications */}
            <section className="space-y-3">
              <AdminPanelTitle className="!mb-0">Clarifications</AdminPanelTitle>
              <DashboardMainContainer className="p-4 sm:p-6">
                {clarifications.length === 0 ? (
                  <AdminSupportingText className="text-mediumgray">
                    No accepted clarifications for this Request.
                  </AdminSupportingText>
                ) : (
                  <div className="space-y-6">
                    {clarifications.map((item, idx) => (
                      <div
                        key={item.clarificationId || idx}
                        className="space-y-3 pb-6 border-b border-lightgray last:border-b-0 last:pb-0"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <Field label="Clarification ID">{displayOrDash(item.clarificationId)}</Field>
                          <Field label="Customer">{displayOrDash(item.customerName)}</Field>
                          <Field label="Submitted On">{formatDateTime(item.submittedOn)}</Field>
                          <Field label="Form Version">{displayOrDash(item.formVersion)}</Field>
                          <Field label="Used for Sending">
                            {item.usedForSending ? 'Yes' : 'No'}
                          </Field>
                        </div>
                        <div className="space-y-2">
                          <AdminDetailFieldLabel>Changed Values</AdminDetailFieldLabel>
                          {item.changedValues && typeof item.changedValues === 'object' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {Object.entries(item.changedValues).map(([key, val]) => (
                                <Field key={key} label={key}>
                                  {displayOrDash(val)}
                                </Field>
                              ))}
                            </div>
                          ) : (
                            <AdminDetailFieldValue>—</AdminDetailFieldValue>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </DashboardMainContainer>
            </section>

            {/* Outcome */}
            <section className="space-y-3">
              <AdminPanelTitle className="!mb-0">Outcome</AdminPanelTitle>
              <DashboardMainContainer className="p-4 sm:p-6 space-y-4">
                <Field label="Result">{displayOrDash(outcome?.result || request.requestStatus)}</Field>
                <Field label="Explanation">
                  {displayOrDash(outcome?.explanation)}
                </Field>
                {assessments.length === 0 ? (
                  <AdminSupportingText className="text-mediumgray">
                    No Website assessment Rule was applied, or no usable Rule was available.
                  </AdminSupportingText>
                ) : (
                  assessments.map((assessment, idx) => (
                    <div
                      key={`${assessment.ruleId || 'rule'}-${idx}`}
                      className="space-y-3 pt-4 border-t border-lightgray first:border-t-0 first:pt-0"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1 min-w-0">
                          <AdminDetailFieldLabel>Rule ID</AdminDetailFieldLabel>
                          {renderCopyable(`ruleId-${idx}`, assessment.ruleId, 'Rule ID')}
                        </div>
                        <Field label="Assessment Time">
                          {formatDateTime(assessment.assessedOn)}
                        </Field>
                      </div>
                      <div className="space-y-2">
                        <AdminDetailFieldLabel>Applied Rule Settings</AdminDetailFieldLabel>
                        <AppliedRuleSettingsBlock settings={assessment.appliedRuleSettings} />
                      </div>
                    </div>
                  ))
                )}
              </DashboardMainContainer>
            </section>

            {/* Recipients */}
            <section className="space-y-3">
              <AdminPanelTitle className="!mb-0">Recipients</AdminPanelTitle>
              <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
                {recipients.length === 0 ? (
                  <div className="p-4">
                    <AdminSupportingText className="text-mediumgray">
                      No recipients were selected for this Request.
                    </AdminSupportingText>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="min-w-[640px]">
                      <div
                        className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3"
                        style={{ gridTemplateColumns: RECIPIENTS_GRID }}
                      >
                        <AdminTableHeader>Business</AdminTableHeader>
                        <AdminTableHeader>Delivery Result</AdminTableHeader>
                        <AdminTableHeader>Delivered On</AdminTableHeader>
                        <AdminTableHeader>Quote</AdminTableHeader>
                      </div>
                      {recipients.map((row, idx) => {
                        const businessUrl = row.businessId
                          ? `/admin/users/businesses/${row.businessId}`
                          : null;
                        const quoteUrl = row.quoteId
                          ? `/admin/quotes/all/${row.quoteId}`
                          : null;
                        return (
                          <div
                            key={`${row.businessId || 'biz'}-${row.quoteId || idx}`}
                            className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3"
                            style={{ gridTemplateColumns: RECIPIENTS_GRID }}
                          >
                            <div className="flex items-center min-w-0">
                              <AdminTableBody className="truncate" title={row.businessName}>
                                {businessUrl ? (
                                  <AdminLinkAction to={businessUrl}>
                                    {row.businessName || row.businessId}
                                  </AdminLinkAction>
                                ) : (
                                  row.businessName || '—'
                                )}
                              </AdminTableBody>
                            </div>
                            <div className="flex items-center min-w-0">
                              <AdminTableBody>{row.deliveryResult || '—'}</AdminTableBody>
                            </div>
                            <div className="flex items-center min-w-0">
                              <AdminTableMeta>{formatDateTime(row.deliveredOn)}</AdminTableMeta>
                            </div>
                            <div className="flex items-center min-w-0">
                              <AdminTableBody>
                                {quoteUrl ? (
                                  <AdminLinkAction to={quoteUrl}>{row.quoteId}</AdminLinkAction>
                                ) : (
                                  '—'
                                )}
                              </AdminTableBody>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </DashboardMainContainer>
            </section>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default QuoteRequestDetail;
